/**
 * カードデザインシステム定数
 * 設計書: src/ルール/ui/card-design-system.md
 */

// ========================================
// 基準サイズ
// ========================================

export const CARD_BASE_SIZE = {
  /** 手札・フィールド用（軽量） */
  compact: { width: 750, height: 975 },

  /** 詳細・図鑑用（高品質） */
  detail: { width: 1500, height: 1950 },
} as const;

// ========================================
// 表示モード
// ========================================

export type CardDisplayMode = 'hand' | 'field' | 'detail' | 'dex' | 'dex-list' | 'collection' | 'pack' | 'deckBuilder';

export const CARD_DISPLAY_CONFIG: Record<CardDisplayMode, {
  baseSize: 'compact' | 'detail';
  showCost: boolean;
  showName: boolean;
  showCategory: boolean;
  showAttributeIcon: boolean;
  showTypeIcon: boolean;
  showSkillIcons: boolean;
  showStats: boolean;
  showEffect: boolean;
}> = {
  hand: {
    baseSize: 'compact',
    showCost: true,
    showName: true,
    showCategory: false,
    showAttributeIcon: true,
    showTypeIcon: true,
    showSkillIcons: true,
    showStats: true,
    showEffect: true,
  },
  field: {
    baseSize: 'compact',
    showCost: false,
    showName: false,
    showCategory: false,
    showAttributeIcon: true,
    showTypeIcon: false,
    showSkillIcons: true,
    showStats: true,
    showEffect: false,
  },
  detail: {
    baseSize: 'compact',
    showCost: true,
    showName: true,
    showCategory: true,
    showAttributeIcon: true,
    showTypeIcon: true,
    showSkillIcons: true,
    showStats: true,
    showEffect: true,
  },
  dex: {
    baseSize: 'detail',
    showCost: true,
    showName: true,
    showCategory: true,
    showAttributeIcon: true,
    showTypeIcon: true,
    showSkillIcons: true,
    showStats: true,
    showEffect: true,
  },
  /** 図鑑一覧用（カード本体のUI要素は非表示、オーバーレイで別管理） */
  'dex-list': {
    baseSize: 'compact',
    showCost: false,
    showName: false,
    showCategory: false,
    showAttributeIcon: false,
    showTypeIcon: false,
    showSkillIcons: false,
    showStats: false,
    showEffect: false,
  },
  /** コレクション画面用（カード本体のUI要素は非表示、CollectionCardが管理） */
  collection: {
    baseSize: 'compact',
    showCost: false,
    showName: false,
    showCategory: false,
    showAttributeIcon: false,
    showTypeIcon: false,
    showSkillIcons: false,
    showStats: false,
    showEffect: false,
  },
  /** パック開封画面用（コンパクト表示、レアリティバッジは外部で管理） */
  pack: {
    baseSize: 'compact',
    showCost: true,
    showName: true,
    showCategory: false,
    showAttributeIcon: true,
    showTypeIcon: false,
    showSkillIcons: false,
    showStats: true,
    showEffect: false,
  },
  /** デッキ構築画面用（コンパクト表示、使用枚数は外部で管理） */
  deckBuilder: {
    baseSize: 'compact',
    showCost: true,
    showName: true,
    showCategory: false,
    showAttributeIcon: true,
    showTypeIcon: false,
    showSkillIcons: false,
    showStats: false,  // 下部UIで表示するためCardV2では非表示
    showEffect: false,
  },
};

// ========================================
// UI要素の位置（750x975基準）
// ========================================

export const CARD_LAYOUT = {
  /** コスト（solid枠の黒丸の中央に配置） */
  cost: {
    x: 32,
    y: 28,
    size: 110,
    fontSize: 66,
  },

  /** カード名 */
  name: {
    y: 32,
    fontSize: 44,
    maxWidth: 480,
  },

  /** カテゴリ */
  category: {
    y: 75,
    fontSize: 24,
  },

  /** 属性アイコン */
  attributeIcon: {
    xFromRight: 30,
    y: 20,
    size: 80,
  },

  /** タイプアイコン（コストの下、カード種別を一目で識別） */
  typeIcon: {
    x: 32,
    y: 150,  // コスト(y:28 + size:110) の下
    size: 90,
    fontSize: 50,
  },

  /** 技アイコン（タイプアイコンの右隣） */
  skillIcons: {
    x: 110,  // typeIcon.x + typeIcon.size + gap
    y: 555,
    size: 40,
    gap: 10,
    fontSize: 24,
  },

  /** ATK */
  atk: {
    x: 50,
    y: 865,
    fontSize: 68,
  },

  /** HP */
  hp: {
    xFromRight: 50,
    y: 865,
    fontSize: 68,
  },

  /** 効果テキスト */
  effect: {
    x: 45,
    y: 610,
    width: 660,
    height: 230,
    fontSize: 28,
    lineHeight: 1.35,
  },

  /** カード枠の領域（1500x1950基準 → 750x975に変換済み） */
  regions: {
    topFrame: { y: 0, height: 40 },
    illustrationArea: { y: 40, height: 460 },
    dividerLine: { y: 500, height: 25 },
    effectArea: { y: 525, height: 400 },
    logoArea: { y: 925, height: 50 },
  },
} as const;

// ========================================
// 表示サイズプリセット
// ========================================

export const CARD_DISPLAY_SIZES = {
  /** モバイル手札（自分） */
  handMobileSelf: { width: 90, height: 117 },

  /** モバイル手札（相手） */
  handMobileOpponent: { width: 70, height: 91 },

  /** デスクトップ手札（自分） - 大きめ */
  handDesktopSelf: { width: 140, height: 182 },

  /** デスクトップ手札（相手） */
  handDesktopOpponent: { width: 100, height: 130 },

  /** @deprecated 後方互換用 - handDesktopOpponent を使用 */
  handMobile: { width: 70, height: 91 },

  /** @deprecated 後方互換用 - handDesktopOpponent を使用 */
  handDesktop: { width: 100, height: 130 },

  /** モバイルフィールド */
  fieldMobile: { width: 65, height: 85 },

  /** デスクトップフィールド */
  fieldDesktop: { width: 100, height: 130 },

  /** 詳細モーダル */
  detailModal: { width: 300, height: 390 },

  /** 図鑑フル表示 */
  dexFull: { width: 500, height: 650 },

  /** 図鑑一覧（デスクトップ） */
  dexList: { width: 120, height: 156 },

  /** 図鑑一覧（モバイル） */
  dexListMobile: { width: 100, height: 130 },

  /** コレクション画面（デスクトップ） */
  collection: { width: 120, height: 160 },

  /** コレクション画面（モバイル） */
  collectionMobile: { width: 85, height: 113 },

  /** パック開封（デスクトップ） */
  pack: { width: 140, height: 182 },

  /** パック開封（モバイル） */
  packMobile: { width: 90, height: 117 },

  /** デッキ構築（デスクトップ） */
  deckBuilder: { width: 90, height: 117 },

  /** デッキ構築（モバイル） */
  deckBuilderMobile: { width: 75, height: 98 },
} as const;

// ========================================
// スケール計算
// ========================================

/**
 * 表示幅から scale 値を計算
 */
export const getCardScale = (
  targetWidth: number,
  baseSize: 'compact' | 'detail' = 'compact'
): number => {
  const base = CARD_BASE_SIZE[baseSize];
  return targetWidth / base.width;
};

/**
 * 表示サイズを計算（アスペクト比維持）
 */
export const getCardDisplaySize = (
  targetWidth: number,
  baseSize: 'compact' | 'detail' = 'compact'
): { width: number; height: number } => {
  const base = CARD_BASE_SIZE[baseSize];
  const scale = targetWidth / base.width;
  return {
    width: targetWidth,
    height: Math.round(base.height * scale),
  };
};

// ========================================
// テキストスタイル
// ========================================

export const TEXT_STYLES = {
  /** 黒縁取り（標準） */
  outline: {
    textShadow: [
      '-2px -2px 0 #000',
      '2px -2px 0 #000',
      '-2px 2px 0 #000',
      '2px 2px 0 #000',
    ].join(', '),
  },

  /** 黒縁取り（太め） */
  outlineThick: {
    textShadow: [
      '-3px -3px 0 #000',
      '3px -3px 0 #000',
      '-3px 3px 0 #000',
      '3px 3px 0 #000',
      '0px -3px 0 #000',
      '0px 3px 0 #000',
      '-3px 0px 0 #000',
      '3px 0px 0 #000',
    ].join(', '),
  },

  /** 白縁取り（効果テキスト用） */
  outlineWhite: {
    textShadow: [
      '-1px -1px 0 #fff',
      '1px -1px 0 #fff',
      '-1px 1px 0 #fff',
      '1px 1px 0 #fff',
      '0px -1px 0 #fff',
      '0px 1px 0 #fff',
      '-1px 0px 0 #fff',
      '1px 0px 0 #fff',
    ].join(', '),
  },
} as const;

// ========================================
// コストの色
// ========================================

export const COST_COLORS = {
  normal: {
    background: 'linear-gradient(135deg, #6b4ce6 0%, #9d4ce6 100%)',
    shadow: '0 2px 8px rgba(0,0,0,0.4)',
  },
  reduced: {
    background: 'linear-gradient(135deg, #22c55e 0%, #4ade80 100%)',
    shadow: '0 2px 8px rgba(34, 197, 94, 0.6)',
  },
  increased: {
    background: 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)',
    shadow: '0 2px 8px rgba(239, 68, 68, 0.6)',
  },
} as const;

// ========================================
// 技アイコンの色
// ========================================

export const SKILL_ICON_COLORS = {
  basic: {
    background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
  },
  advanced: {
    background: 'linear-gradient(135deg, #ff9800 0%, #ffa726 100%)',
  },
} as const;

// ========================================
// タイプアイコンの背景色
// ========================================

export const TYPE_ICON_COLORS: Record<string, {
  background: string;
  shadow: string;
}> = {
  monster: {
    background: 'linear-gradient(135deg, #e65100 0%, #ff8f00 100%)',
    shadow: '0 2px 8px rgba(230, 81, 0, 0.6)',
  },
  magic: {
    background: 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 100%)',
    shadow: '0 2px 8px rgba(123, 31, 162, 0.6)',
  },
  field: {
    background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
    shadow: '0 2px 8px rgba(46, 125, 50, 0.6)',
  },
  phasecard: {
    background: 'linear-gradient(135deg, #0097a7 0%, #00bcd4 100%)',
    shadow: '0 2px 8px rgba(0, 151, 167, 0.6)',
  },
} as const;

// ========================================
// 手札扇形表示設定
// 設計書: src/ルール/ui/hand-fan-display-design.md
// ========================================

export interface ArcConfig {
  /** 最大角度幅（度） - 全カードが占める角度 */
  maxAngleSpread: number;
  /** カード間の最小重なり幅（px） */
  minOverlap: number;
  /** カード間の最大重なり幅（px） */
  maxOverlap: number;
  /** 端カードの最大Y下降量（px） */
  maxEdgeDropY: number;
}

/** デスクトップ用弧設定 */
export const DESKTOP_ARC: ArcConfig = {
  maxAngleSpread: 25,
  minOverlap: 15,
  maxOverlap: 50,
  maxEdgeDropY: 12,
};

/** モバイル用弧設定 */
export const MOBILE_ARC: ArcConfig = {
  maxAngleSpread: 20,
  minOverlap: 20,
  maxOverlap: 45,
  maxEdgeDropY: 8,
};

export interface CardPosition {
  /** 左からの位置（px） */
  x: number;
  /** 上からの位置 - 弧のための調整（px） */
  y: number;
  /** 回転角度（度） */
  rotation: number;
  /** 重なり順 - 左が奥(小)、右が手前(大) */
  zIndex: number;
}

/**
 * 各カードの弧状配置位置を計算
 * @param cardCount カード枚数
 * @param containerWidth コンテナ幅
 * @param cardWidth カード幅
 * @param cardHeight カード高さ
 * @param config 弧設定
 */
export function calculateCardPositions(
  cardCount: number,
  containerWidth: number,
  cardWidth: number,
  cardHeight: number,
  config: ArcConfig
): CardPosition[] {
  if (cardCount === 0) return [];

  // 1枚の場合は中央に配置
  if (cardCount === 1) {
    return [{
      x: (containerWidth - cardWidth) / 2,
      y: 0,
      rotation: 0,
      zIndex: 1,
    }];
  }

  const positions: CardPosition[] = [];

  // 角度計算（枚数に応じて角度幅を調整）
  const angleSpread = Math.min(config.maxAngleSpread, cardCount * 3.5);
  const startAngle = -angleSpread / 2;
  const angleStep = angleSpread / (cardCount - 1);

  // 重なり計算（枚数に応じて重なり増加）
  const overlapIncrease = Math.min(
    config.maxOverlap - config.minOverlap,
    (cardCount - 2) * 5
  );
  const overlap = config.minOverlap + overlapIncrease;

  // 全体幅と開始位置
  const totalWidth = cardWidth + (cardCount - 1) * (cardWidth - overlap);
  const startX = (containerWidth - totalWidth) / 2;

  for (let i = 0; i < cardCount; i++) {
    // 回転角度
    const rotation = startAngle + angleStep * i;

    // X位置
    const x = startX + i * (cardWidth - overlap);

    // Y位置（弧に沿って端が下がる）
    const normalizedPos = cardCount > 1
      ? (i - (cardCount - 1) / 2) / ((cardCount - 1) / 2)
      : 0;
    const y = Math.abs(normalizedPos) * config.maxEdgeDropY;

    // z-index: 左が奥(小)、右が手前(大)
    const zIndex = (i + 1) * 10;

    positions.push({ x, y, rotation, zIndex });
  }

  return positions;
}

/** ホバー/選択時のインタラクション設定 */
export const HAND_INTERACTION = {
  /** ホバー時のY移動量（上に飛び出す） */
  hoverLiftY: -15,
  /** 選択時のY移動量 */
  selectedLiftY: -25,
  /** 選択時のz-index */
  selectedZIndex: 100,
  /** トランジション時間 */
  transitionDuration: '0.2s',
  /** トランジションイージング */
  transitionEasing: 'ease-out',
} as const;

// ========================================
// レスポンシブ手札サイズ計算
// ========================================

export interface ResponsiveHandSize {
  /** 計算後のカード幅 */
  cardWidth: number;
  /** 計算後のカード高さ */
  cardHeight: number;
  /** カード間の重なり量 */
  overlap: number;
}

/**
 * コンテナ幅に応じた手札カードサイズと重なりを計算（ハイブリッド方式）
 *
 * 1. まず重なりを増やして収める
 * 2. 重なり限界（65%）に達したらカードサイズを縮小
 *
 * @param containerWidth 手札エリアの利用可能幅
 * @param cardCount 手札枚数
 * @param baseCardWidth 基準カード幅
 * @param minCardWidth 最小カード幅（これ以下には縮小しない）
 */
export function calculateResponsiveHandSize(
  containerWidth: number,
  cardCount: number,
  baseCardWidth: number,
  minCardWidth: number = 70
): ResponsiveHandSize {
  const aspectRatio = 1.3; // カードのアスペクト比（高さ = 幅 × 1.3）

  // 0-1枚の場合は重なりなし
  if (cardCount <= 1) {
    return {
      cardWidth: baseCardWidth,
      cardHeight: Math.round(baseCardWidth * aspectRatio),
      overlap: 0,
    };
  }

  const maxOverlapRatio = 0.65; // カード幅の65%まで重なり許容
  const minOverlap = 15; // 最小重なり
  const maxOverlap = baseCardWidth * maxOverlapRatio;

  // 最大重なりで必要な最小幅を計算
  // totalWidth = cardWidth + (cardCount - 1) * (cardWidth - overlap)
  // totalWidth = cardWidth * cardCount - overlap * (cardCount - 1)
  const minWidthNeeded = baseCardWidth * cardCount - maxOverlap * (cardCount - 1);

  if (containerWidth >= minWidthNeeded) {
    // 段階1: 重なり調整だけで収まる
    // containerWidth = cardWidth * cardCount - overlap * (cardCount - 1)
    // overlap = (cardWidth * cardCount - containerWidth) / (cardCount - 1)
    const neededOverlap = (baseCardWidth * cardCount - containerWidth) / (cardCount - 1);
    const finalOverlap = Math.max(minOverlap, Math.min(maxOverlap, neededOverlap));

    return {
      cardWidth: baseCardWidth,
      cardHeight: Math.round(baseCardWidth * aspectRatio),
      overlap: finalOverlap,
    };
  }

  // 段階2: サイズ縮小が必要
  // containerWidth = cardWidth * cardCount - (cardWidth * maxOverlapRatio) * (cardCount - 1)
  // containerWidth = cardWidth * (cardCount - maxOverlapRatio * (cardCount - 1))
  // cardWidth = containerWidth / (cardCount - maxOverlapRatio * (cardCount - 1))
  const divisor = cardCount - maxOverlapRatio * (cardCount - 1);
  const scaledWidth = containerWidth / divisor;
  const finalWidth = Math.max(minCardWidth, Math.round(scaledWidth));
  const finalOverlap = finalWidth * maxOverlapRatio;

  return {
    cardWidth: finalWidth,
    cardHeight: Math.round(finalWidth * aspectRatio),
    overlap: finalOverlap,
  };
}

// ========================================
// 図鑑一覧用スタイル定数
// ========================================

/** 図鑑一覧の下部テキストエリア高さ */
export const DEX_LIST_BOTTOM_INFO_HEIGHT = 48;

/** 図鑑一覧用スタイル */
export const DEX_LIST_STYLES = {
  /** 所持バッジ */
  ownedBadge: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    background: '#4caf50',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 'bold' as const,
    zIndex: 5,
  },

  /** 未所持マーク */
  notOwnedMark: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: 'rgba(160, 160, 160, 0.8)',
    fontSize: 48,
    fontWeight: 'bold' as const,
    zIndex: 5,
    textShadow: '0 2px 8px rgba(0,0,0,0.5)',
  },

  /** ウィッシュリストハート */
  wishlistHeart: {
    position: 'absolute' as const,
    top: 8,
    right: 8,
    background: 'rgba(0, 0, 0, 0.5)',
    border: 'none',
    borderRadius: '50%',
    width: 28,
    height: 28,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    transition: 'transform 0.15s ease',
  },

  /** 下部情報コンテナ */
  bottomInfo: {
    padding: '8px 4px 4px',
    textAlign: 'center' as const,
  },

  /** 下部カード名 */
  bottomName: {
    fontSize: 13,
    fontWeight: 'bold' as const,
    color: '#e0e0e0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },

  /** 下部コスト */
  bottomCost: {
    fontSize: 11,
    color: '#a0a0a0',
    marginTop: 2,
  },

  /** 所持時のカードスタイル */
  cardOwned: {
    opacity: 1,
    filter: 'none',
    borderColor: 'rgba(76, 175, 80, 0.5)',
  },

  /** 未所持時のカードスタイル */
  cardNotOwned: {
    opacity: 0.5,
    filter: 'grayscale(0.5)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },

  /** ホバー時のスタイル */
  cardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(107, 76, 230, 0.3)',
  },
} as const;
